import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import updatePlatformConfigs from 'redux/actions/Common/updatePlatformConfigs';
import backfillAiResponseSections from 'redux/actions/Common/backfillAiResponseSections';

/**
 * The AI sections a weekly deal summary run can ask the model for. Each one is
 * an `{ xmlTag, description }` pair: the tag the model is told to wrap its
 * answer in, and the instruction itself.
 *
 * Leaving a section unset means it is never requested, and the record type it
 * feeds is never created for the org.
 */
const AI_RESPONSE_SECTIONS = [
  {
    key: 'top3Highlights',
    label: 'Top 3 Highlights',
    defaultXmlTag: 'top_3_deal_highlights',
    helperText:
      'Saved on the PERIOD_SUMMARY record as its top highlights. The period summary itself is always generated.',
  },
  {
    key: 'riskScore',
    label: 'Risk Score',
    defaultXmlTag: 'risk_score',
    helperText:
      'A 1-100 risk-of-loss score. Configure it together with Top Risk Reasons to get a RISK_REPORT record.',
  },
  {
    key: 'topRiskReasons',
    label: 'Top Risk Reasons',
    defaultXmlTag: 'top_risk_reasons',
    helperText:
      'The reasoning behind the risk score. Saved per deal on the RISK_REPORT.',
  },
  {
    key: 'riskMitigation',
    label: 'Risk Mitigation',
    defaultXmlTag: 'risk_mitigation',
    helperText:
      "What to actually do about each risky deal. Saved per deal on the RISK_REPORT alongside its reasons, and shown on the deal's risk page in the app. Left unset, the section is never asked for and that page shows no mitigations.",
  },
  {
    key: 'emailsThatNeedsAttention',
    label: 'Emails That Need Attention',
    defaultXmlTag: 'emails_that_need_attention',
    helperText:
      'Creates the EMAILS_NEEDING_ATTENTION record. Ids only: the description must instruct the model to return nothing but a comma separated list of interaction ids, otherwise the answer will not parse.',
    idsOnly: true,
  },
];

const emptySection = (section) => ({
  enabled: false,
  xmlTag: section.defaultXmlTag,
  description: '',
});

/** Platform config -> editor state for every section. */
const toSectionState = (platformConfigs) =>
  AI_RESPONSE_SECTIONS.reduce((acc, section) => {
    const saved = platformConfigs?.[section.key];
    acc[section.key] = saved
      ? {
          enabled: true,
          xmlTag: saved?.xmlTag || section.defaultXmlTag,
          description: saved?.description || '',
        }
      : emptySection(section);
    return acc;
  }, {});

/**
 * Editor for the weekly summary AI response sections on the platform config.
 * Which sections are configured here decides which of the three weekly record
 * types (PERIOD_SUMMARY / RISK_REPORT / EMAILS_NEEDING_ATTENTION) a generation
 * run produces.
 */
const AiResponseSections = ({ platformConfigs, onUpdated }) => {
  const dispatch = useDispatch();
  const [sections, setSections] = useState(() =>
    toSectionState(platformConfigs),
  );
  const [updating, setUpdating] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [confirmingBackfill, setConfirmingBackfill] = useState(false);

  useEffect(() => {
    if (platformConfigs) setSections(toSectionState(platformConfigs));
  }, [platformConfigs]);

  const updateSection = (key, changes) =>
    setSections((prev) => ({ ...prev, [key]: { ...prev[key], ...changes } }));

  // An enabled section needs both halves: the tag the model is told to use and
  // the instruction that goes with it.
  const isIncomplete = (key) => {
    const value = sections[key];
    return (
      value?.enabled && (!value?.xmlTag?.trim() || !value?.description?.trim())
    );
  };
  const hasIncompleteSection = AI_RESPONSE_SECTIONS.some((section) =>
    isIncomplete(section.key),
  );

  const handleUpdate = () => {
    const payload = AI_RESPONSE_SECTIONS.reduce((acc, section) => {
      const value = sections[section.key];
      // `null` clears the section, which stops that report type being
      // generated at all.
      acc[section.key] = value?.enabled
        ? {
            xmlTag: value.xmlTag.trim(),
            description: value.description.trim(),
          }
        : null;
      return acc;
    }, {});

    setUpdating(true);
    dispatch(
      updatePlatformConfigs(payload, () => {
        setUpdating(false);
        alert('Information Updated');
        if (typeof onUpdated === 'function') onUpdated();
      }),
    );
  };

  const handleBackfill = () => {
    setConfirmingBackfill(false);
    setBackfilling(true);
    dispatch(
      backfillAiResponseSections(
        () => {
          setBackfilling(false);
          alert('Defaults seeded for orgs that had no sections configured.');
          if (typeof onUpdated === 'function') onUpdated();
        },
        () => {
          setBackfilling(false);
          alert('Failed to seed the defaults.');
        },
      ),
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        border: '1px solid #d3d3d3',
        borderRadius: 2,
        padding: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
          gap: 2,
        }}
      >
        <Box>
          <Typography>Weekly Summary AI Sections</Typography>
          <Typography variant="caption" color="text.secondary">
            Sections requested from the AI on every weekly deal summary run. A
            section left unchecked is never asked for, and the report type it
            feeds is never created.
          </Typography>
        </Box>
        <Box display={'flex'} gap={1}>
          <Button
            variant="outlined"
            size="small"
            disabled={backfilling}
            onClick={() => setConfirmingBackfill(true)}
          >
            {backfilling ? 'Seeding...' : 'Seed Defaults'}
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={updating || hasIncompleteSection}
            onClick={handleUpdate}
          >
            {updating ? 'Updating...' : 'Update'}
          </Button>
        </Box>
      </Box>
      <Divider orientation="horizontal" flexItem />
      {AI_RESPONSE_SECTIONS.map((section) => {
        const value = sections[section.key] || emptySection(section);
        return (
          <Box
            key={section.key}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              border: '1px solid #f1f1f1',
              borderRadius: 2,
              padding: 2,
            }}
          >
            <Box
              display={'flex'}
              alignItems={'center'}
              justifyContent={'space-between'}
              gap={1}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!value.enabled}
                    name={section.key}
                    onChange={(e) =>
                      updateSection(section.key, {
                        enabled: e.target.checked,
                      })
                    }
                  />
                }
                label={
                  <Typography variant="subtitle2">{section.label}</Typography>
                }
              />
              {section.idsOnly ? (
                <Chip size="small" color="warning" label="ids only" />
              ) : null}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {section.helperText}
            </Typography>
            <TextField
              label="XML Tag"
              variant="outlined"
              size="small"
              fullWidth
              disabled={!value.enabled}
              value={value.xmlTag}
              placeholder={section.defaultXmlTag}
              error={value.enabled && !value.xmlTag?.trim()}
              helperText={
                value.enabled && !value.xmlTag?.trim()
                  ? 'A tag is required while the section is enabled.'
                  : 'The tag the model is told to wrap this section in.'
              }
              onChange={(e) =>
                updateSection(section.key, { xmlTag: e.target.value })
              }
            />
            <TextField
              label="Description"
              variant="outlined"
              size="small"
              fullWidth
              multiline
              minRows={3}
              disabled={!value.enabled}
              value={value.description}
              error={value.enabled && !value.description?.trim()}
              helperText={
                value.enabled && !value.description?.trim()
                  ? 'An instruction is required while the section is enabled.'
                  : 'The instruction the AI is given for this section.'
              }
              onChange={(e) =>
                updateSection(section.key, { description: e.target.value })
              }
            />
          </Box>
        );
      })}
      {hasIncompleteSection ? (
        <Typography variant="caption" color="error">
          Fill in the tag and the description of every enabled section before
          updating.
        </Typography>
      ) : null}

      {/* Seeding writes to every org that has none of these configured, so it
          is confirmed rather than run straight off the button. */}
      <Dialog
        open={confirmingBackfill}
        onClose={() => setConfirmingBackfill(false)}
        aria-labelledby="seed-defaults-dialog-title"
        aria-describedby="seed-defaults-dialog-description"
      >
        <DialogTitle id="seed-defaults-dialog-title">
          Seed default AI sections?
        </DialogTitle>
        <DialogContent>
          <Box
            id="seed-defaults-dialog-description"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              maxWidth: 480,
            }}
          >
            <Typography variant="body2">
              This writes the default tag and instruction for every section
              above to each organization that has none of them configured yet.
            </Typography>
            <Box
              sx={{
                borderRadius: 1,
                border: '1px solid #f1f1f1',
                borderLeft: '3px solid',
                borderLeftColor: 'warning.main',
                padding: 1.5,
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                textTransform="uppercase"
              >
                Note
              </Typography>
              <Typography variant="body2" mt={0.5}>
                Organizations that already have sections configured are left
                untouched, so this is safe to re-run. Every organization it does
                seed starts producing the report types those sections feed on
                its next weekly summary run.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setConfirmingBackfill(false)}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={backfilling}
            onClick={handleBackfill}
          >
            Seed Defaults
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

AiResponseSections.propTypes = {
  platformConfigs: PropTypes.object,
  onUpdated: PropTypes.func,
};

export default AiResponseSections;
